import { Money, Transfer, V2ATransfer } from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack } from 'expo-router';
import { isPositiveValue } from 'features/accounts/AccountDetails/AccountDetails.helpers';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionList, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { TagProps } from 'shared/components/vxl/Tag/Tag';
import TransactionListItem from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { HomeTileTypeIds } from 'shared/hooks/homeTiles/HomeTileTypeIds';
import useRefreshHomeTiles from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { capitalizeWord } from 'shared/utils/string/string.helpers';

import { groupTransactionsByMonth, transactionStatus } from './transfer.helper';

interface TransferHistoryWrapperProps<T> {
  title: string;
  transferList: T[];
  error: boolean;
  loading: boolean;
  loadMore: () => void;
  onPressItem: (item: T) => void;
  renderEmptyState: () => React.ReactElement;
  renderTitle: (item: T) => string; // TODO: JT-122277 add default definition
  renderSecondaryText: (item: T) => string; // TODO: JT-122277 add default definition
  renderTags?: (item: T) => TagProps[] | undefined; // TODO: JT-122277 add default definition
  emptyScreenType?: 'v2a' | 'move-money';
}

const LOAD_MORE_THRESHOLD = 20;

const TransferHistoryWrapper = <T extends { id: string; value: Money }>({
  title,
  transferList,
  error,
  loading,
  loadMore,
  onPressItem,
  renderEmptyState,
  renderTitle,
  renderSecondaryText,
  renderTags,
}: TransferHistoryWrapperProps<T>) => {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'transferHistory',
  });
  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const insets = useSafeAreaInsets();

  const [isNavigating, setIsNavigating] = useState(false);
  useEffect(() => {
    if (transferList?.length < LOAD_MORE_THRESHOLD) {
      loadMore();
    }
  }, [loadMore, transferList?.length]);

  const { refreshTiles } = useRefreshHomeTiles([
    HomeTileTypeIds.VXL_CARD_GROUP_TILE_FOR_YOU,
  ]);

  const handlePressItem = (item: T) => {
    if (isNavigating) {
      return;
    }
    setIsNavigating(true);
    onPressItem(item);
    setTimeout(() => {
      setIsNavigating(false);
    }, 1000);
  };

  useEffect(() => {
    refreshTiles();
  }, [refreshTiles]);

  const contentContainerStyle = [
    styles.scrollView,
    transferList.length > 0 ? { paddingBottom: insets.bottom } : null,
  ];
  const transactionGroupBy = groupTransactionsByMonth(
    transferList as unknown as (Transfer | V2ATransfer)[],
    translatedText('pendingStatus'),
  );

  return (
    <View
      style={[
        styles.container,
        {
          paddingBottom: transferList.length > 0 ? insets.bottom : undefined,
        },
      ]}
    >
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScreenTransitionOverlay
        isLoading={loading && transferList?.length === 0}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={defaultMessage}
              illustrationKey="PLACEHOLDER_A"
              tryAgainAction={() => {}}
            />
          ) : null
        }
      />
      <SectionList
        sections={transactionGroupBy}
        keyExtractor={(item, index) =>
          item.id ? item.id.toString() : `${index}`
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.1}
        windowSize={transferList.length > 0 ? transferList.length : 21}
        renderSectionHeader={({ section: { title } }) => (
          <SectionDivider labelText={capitalizeWord(title)} />
        )}
        renderItem={({ item }) => {
          const status: string = item.status.toLocaleLowerCase() as string;
          const isPending: boolean = transactionStatus.includes(
            status as string,
          );
          const amountFormatted: string = item.value
            .formattedWithPlusPrefix as string;
          const tags = renderTags?.(item as unknown as T)?.filter(
            (tag) => tag.text && tag.type,
          );
          return (
            <AmplitudeMask>
              <TransactionListItem
                amountStyle={isPositiveValue(
                  item.value.formattedWithPlusPrefix,
                )}
                isPending={isPending}
                titleText={renderTitle(item as unknown as T)}
                amountText={amountFormatted}
                accessibilityLabel="transfer-list-item"
                secondaryText={renderSecondaryText(item as unknown as T)}
                tags={!isPending ? tags : undefined}
                onPress={() => handlePressItem(item as unknown as T)}
              />
            </AmplitudeMask>
          );
        }}
        ItemSeparatorComponent={() => (
          <View style={styles.divider}>
            <HairLineDivider />
          </View>
        )}
        contentContainerStyle={contentContainerStyle}
        ListEmptyComponent={() => renderEmptyState()}
        renderSectionFooter={() => <View style={styles.dividerSection} />}
        removeClippedSubviews
        ListFooterComponent={
          loading && transferList?.length === 0 ? (
            <View style={styles.footerLoader}>
              <LoadingSpinner />
            </View>
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  divider: {
    marginHorizontal: theme.spacing.m,
  },
  dividerSection: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  footerLoader: {
    alignItems: 'center',
  },
  scrollView: {
    flexDirection: 'column',
    flexGrow: 1,
  },
});

export default TransferHistoryWrapper;
